import * as React from "react"
import { connect } from "react-redux"
import trans from "trans"
import {PreviewsForm, PreviewsSettings} from "template-previews/types"
import { orderedBySize } from "template-edit/reducers"

import {
    css,
    Radio,
    Theme,
    LittleStatus,
    Grid,
    GridCell,
} from "@bdfx/mergado-ui-kit"

import { updateInputValue as change } from "app/reducers/form"
import {getLocale} from "app/helpers/session";
import {getDateToday} from "app/helpers/other";
import {SizeItem} from "settings/components/sizes-settings";
import {ReduxList} from "app/types/app-types";
import { setWatermarkGroupsToStore, setInpaintingGroupsToStore } from 'watermark-groups/reducers';
import { InpaintingGroupType, WatermarkGroupType } from "products/types"
import { getInpaintingGroups, getWatermarkGroups } from "app/helpers/api"
import { AxiosResponse } from "axios"
import { selectFormValues } from "app/reducers/form"
import { TemplateBadges } from "template-list/template-badges"

export interface Props {
    templates?: any
    previewsForm: PreviewsForm
    change: typeof change
    sizes: ReduxList<SizeItem>
    updatePreviews: Function
    setWatermarkGroupsToStore: typeof setWatermarkGroupsToStore
    setInpaintingGroupsToStore: typeof setInpaintingGroupsToStore
}

type CustomOptionType = {value: string, label: React.JSX.Element}

class TemplatesSidebar extends React.Component<Props> {
    constructor(props: Props) {
        super(props)
    }

    componentDidMount(): void {
        getWatermarkGroups()
            .then((response: AxiosResponse<WatermarkGroupType[]>) => {
                this.props.setWatermarkGroupsToStore(response.data)
            })
            .catch(e => {
                console.error('cannot get watermark groups', e)
            })

        getInpaintingGroups()
            .then((response: AxiosResponse<InpaintingGroupType[]>) => {
                this.props.setInpaintingGroupsToStore(response.data)
            })
            .catch(e => {
                console.error('cannot get inpainting groups', e)
            })
    }

    getActiveValue = () => {
        const { previewsForm } = this.props

        if (previewsForm.view.type !== 'size') {
            return `${previewsForm.view.id}`
        } else if (previewsForm.view.id === 'ALL') {
            return 'ALL'
        } else {
            return `s${previewsForm.view.id}`
        }
    }

    handleSizeChanged = (size : string) => {
        const { previewsForm } = this.props

        if (size === undefined) {
            return false
        }

        const view : PreviewsSettings = {
            ...previewsForm.view,
            page: 1,
            type: (size === 'ALL' || size[0] === 's') ? 'size' : 'template',
            id: (size === 'ALL') ? 'ALL' : (size[0] === 's' ? parseInt(size.substr(1)) : size)
        }

        this.props.change('previewsForm', 'view', view)
        this.props.updatePreviews({...previewsForm, view: view})

        return true
    }

    getOption = (template, activeValue): CustomOptionType => {
        const locale = getLocale().replace('_', '-')
        const dateNow = getDateToday()
        const dateFrom = template.active_from ? new Date(template.active_from) : null
        const dateTo = template.active_to ? new Date(template.active_to) : null
        const isActiveNow = ((!dateFrom || (dateFrom <= dateNow)) && (!dateTo || (dateTo >= dateNow)))
        const activeDate = (template.active_from || template.active_to) ?
            (
                '(' +
                (isActiveNow ? (trans('is_active') + ' ') : '') +
                (dateFrom ? (trans('from_date') + ' ' + dateFrom.toLocaleDateString(locale)) : '') + ' ' +
                (dateTo ? (trans('to_date') + ' ' + dateTo.toLocaleDateString(locale)) : '') +
                ')'
            ) : ''

        return {
            value: `${template.id}`,
            label: <Grid className={`label-grid label-span ${activeValue === `${template.id}` ? 'active' : ''}`}>
                <GridCell>{template.name} {activeDate} <small>({template.products_count})</small></GridCell>
                <GridCell>
                    <LittleStatus
                        type={template.active && isActiveNow ? 'success' : 'inactive'}
                        title={isActiveNow ? trans('is_active') : trans('not_active')}
                        style={{ paddingLeft: '0px' }}
                    />
                </GridCell>
                <GridCell><TemplateBadges template={template} showInline={false} /></GridCell>
            </Grid>,
        }
    }

    render() {
        const {sizes, templates, previewsForm} = this.props
        const activeValue = this.getActiveValue()
        const templatesWithoutSize = templates
                            .filter(template => (!template.size?.id && (previewsForm.view.active === null || template.active === previewsForm.view.active)))
                            .map(template => this.getOption(template, activeValue))

        return (
            <StyledSectionSection>
                <StyledWrapper key={`preview${previewsForm.view.active}`}>
                    <Radio
                        items={[{ value: 'ALL', label: trans("all_templates") }]}
                        onChange={(e) => this.handleSizeChanged(e)}
                        value={activeValue}
                    />

                {sizes.data
                    .filter(size => size && size.name !== null && size.id !== null)
                    .map(size => {
                        const formatTemplates = templates
                            .filter(template => (`${template.size?.id}` === `${size.id}`) && (previewsForm.view.active === null || template.active === previewsForm.view.active))
                            .map(template => this.getOption(template, activeValue))

                        const allTemplatesForFormatItem : CustomOptionType = {value: `s${size?.id}`, label: trans("all_templates")}

                        return <div key={`size${size?.id}`}>
                            {(formatTemplates.length > 0) &&
                                <React.Fragment>
                                    <HeaderDiv>{size.name} ({size.width} × {size.height})</HeaderDiv>
                                    <div>
                                        <Radio
                                            items={[allTemplatesForFormatItem, ...formatTemplates]}
                                            onChange={(e) => this.handleSizeChanged(e)}
                                            value={activeValue}
                                        />
                                    </div>
                                </React.Fragment>
                            }
                        </div>
                    })}

                    {templatesWithoutSize.length > 0 &&
                        <div key={'templates-without-size'}>
                        <HeaderDiv>{trans('is_keep_size')}</HeaderDiv>
                        <div>
                            <Radio
                                items={templatesWithoutSize}
                                onChange={(e) => this.handleSizeChanged(e)}
                                value={activeValue}
                            />
                        </div>
                    </div>
                    }
                </StyledWrapper>
            </StyledSectionSection>
        )
    }
}

const HeaderDiv = css('div')({
    background: Theme.blue,
    color: '#fff',
    padding: '4px',
    fontSize: 'smaller',
    fontWeight: 'bolder',
})

const StyledWrapper = css('div')({
    border: '1px solid #ddd',
    overflowX: 'hidden',
    overflowY: 'auto',
    marginTop: '5px',
    borderRadius: Theme.radius,
    ' .m-button': {
        display: 'flex',
    },
    ' .muk-radio': {
        display: 'inline-grid',
    },
    ' .m-label-wrapper': {
        display: 'inherit',
    },
    ' label:has(.active), label:hover, div.items div.muk-grid.muk-radio.m-item:has(.active), div.items div.muk-grid.muk-radio.m-item:hover': {
        background: '#ccebee',
    },
    ' div.items div.muk-grid.muk-radio.m-item': {
        width: '100%',
        padding: '5px',
    },
    ' img.option-images': {
        maxHeight: '32px',
    },
    ' .label-grid': {
        width: '295px',
        gridTemplateColumns: 'auto min-content',
    },
})

const StyledSectionSection = css('div')({
    overflowX: 'hidden',
    overflowY: 'auto',
    " .muk-form-group": {
        padding: 0,
        marginBottom: '10px',
    },
    " ul": {
        padding: '5px',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        overflowY: 'auto',
        width: 'auto',
    },
    " .muk-inputgroupv__item[data-subheader=true] > label": {
        padding: "2px 10px !important"
    },
    ' label.muk-radio': {
        padding: '15px',
        borderTop: '1px solid rgba(221,221,221,0.2)',
    },
})


function mapStateToProps(state) {
    const templates = orderedBySize(state.templates)

    return {
        templates: templates,
        sizes: state.sizes,
        previewsForm: selectFormValues(state, 'previewsForm'),
    }
}

export default connect(mapStateToProps, {
    change,
    setWatermarkGroupsToStore,
    setInpaintingGroupsToStore,
})(TemplatesSidebar)
